import React from 'react';
import { motion } from 'framer-motion';
import {
  Plug2,
  MessageSquare,
  Code2,
  X,
  Check
} from 'lucide-react';

const comparisons = [
  { traditional: 'Дата-инженеры', us: 'Ни одного найма' },
  { traditional: 'Отдел аналитики', us: 'Инсайты на ИИ' },
  { traditional: 'Месяцы внедрения', us: 'Минуты до старта' },
  { traditional: '$100k+ в год', us: 'Доля от этой суммы' },
];

const integrations = [
  {
    icon: Plug2,
    title: 'Работает с вашим BI',
    description: 'Tableau, Power BI, Looker, Metabase — подключите любой BI за минуту. Команда продолжает работать в привычных инструментах.',
    color: 'bg-[#4361ee]'
  },
  {
    icon: MessageSquare,
    title: 'ИИ-ассистент в чате',
    description: 'Задавайте вопросы обычными словами. Получайте SQL, графики и выводы, не написав ни строчки кода.',
    color: 'bg-[#ff6b35]'
  },
  {
    icon: Code2,
    title: 'Полный доступ по API',
    description: 'Встраивайте аналитику куда угодно: тяните метрики в продукт, автоматизируйте отчёты, собирайте свои дашборды.',
    color: 'bg-[#d4ff00]'
  },
];

export default function ValueProposition() {
  return (
    <section className="bg-[#1a1a1a] py-24 relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="w-full h-full" style={{
          backgroundImage: `radial-gradient(circle, #fff 1px, transparent 1px)`,
          backgroundSize: '30px 30px'
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-block bg-[#ff6b35] text-black px-4 py-2 font-bold text-sm uppercase tracking-wider border-4 border-[#ff6b35] mb-6">
            Почему Evolve
          </div>
          <h2 className="text-[clamp(1.5rem,8.6vw,2.25rem)] md:text-6xl font-black text-white mb-4">
            Команда не нужна.
            <br />
            <span className="text-[#d4ff00]">Инсайты — остаются.</span>
          </h2>
          <p className="text-xl text-white/60 font-medium max-w-2xl mx-auto">
            Классическая аналитика требует целого отдела. Мы его автоматизировали.
          </p>
        </motion.div>

        {/* Comparison table */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white border-4 border-white p-6 md:p-8 shadow-[8px_8px_0_0_#d4ff00] mb-20 max-w-3xl mx-auto"
        >
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="text-center">
              <div className="font-black text-lg text-black/40 uppercase tracking-wider">Как обычно</div>
            </div>
            <div className="text-center">
              <div className="font-black text-lg text-[#d4ff00] uppercase tracking-wider bg-black px-4 py-2 inline-block">С Evolve</div>
            </div>
          </div>

          {comparisons.map((item, i) => (
            <div key={i} className="grid grid-cols-2 gap-4 py-4 border-t-2 border-black/10">
              <div className="flex items-center gap-3">
                <X className="w-5 h-5 text-red-500 flex-shrink-0" />
                <span className="font-medium text-black/60 line-through">{item.traditional}</span>
              </div>
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span className="font-bold text-black">{item.us}</span>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Integration cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h3 className="text-[clamp(1.375rem,7.4vw,1.875rem)] md:text-4xl font-black text-white">
            Встраивается в ваш стек
          </h3>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {integrations.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-[#2a2a2a] border-4 border-[#3a3a3a] p-8 hover:border-[#d4ff00] transition-colors"
            >
              <div className={`w-14 h-14 ${item.color} border-4 border-black flex items-center justify-center mb-6`}>
                <item.icon className="w-7 h-7 text-black" />
              </div>
              <h4 className="text-[clamp(1.05rem,4.8vw,1.25rem)] font-black text-white mb-3">{item.title}</h4>
              <p className="text-white/60 font-medium leading-relaxed">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
